import { todayIn } from "./dates";

// Seasonal themes: a look laid over the light/dark theme (tokens, scenery and
// accents), applied as <html data-seasonal="id">. Each theme is one entry here
// plus its own folder under src/themes/<id>/ (registered in src/themes/index.ts).

export const SEASONAL_THEMES = [
  {
    id: "halloween",
    name: "Halloween",
    blurb: "Jack-o’-lantern glow, bare trees, bats and fog.",
    /** "MM-DD", both ends included; a window may wrap past New Year (e.g. "12-01" to "01-06"). */
    window: { from: "10-01", to: "11-01" },
  },
] as const;

export type SeasonalThemeId = (typeof SEASONAL_THEMES)[number]["id"];
/** "none" = never; "auto" = whichever theme's date window contains today. */
export type SeasonalChoice = "none" | "auto" | SeasonalThemeId;

export function seasonalThemeById(id: string): (typeof SEASONAL_THEMES)[number] | undefined {
  return SEASONAL_THEMES.find((t) => t.id === id);
}

export function isSeasonalChoice(v: unknown): v is SeasonalChoice {
  return v === "none" || v === "auto" || (typeof v === "string" && Boolean(seasonalThemeById(v)));
}

/** Is the "MM-DD" day `md` inside the window (both ends included, wrapping past New Year)? */
export function inWindow(md: string, window: { from: string; to: string }): boolean {
  return window.from <= window.to ? md >= window.from && md <= window.to : md >= window.from || md <= window.to;
}

/** The seasonal theme to show now, or null. The date is the household's own calendar day. */
export function activeSeasonalTheme(choice: SeasonalChoice, now: Date, timeZone: string): SeasonalThemeId | null {
  if (choice === "none") return null;
  if (choice !== "auto") return seasonalThemeById(choice) ? choice : null;
  const md = todayIn(timeZone, now).slice(5);
  return SEASONAL_THEMES.find((t) => inWindow(md, t.window))?.id ?? null;
}

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
/** "Oct 1 – Nov 1" for a window. */
export function seasonDates(window: { from: string; to: string }): string {
  const day = (md: string) => `${MONTHS[Number(md.slice(0, 2)) - 1]} ${Number(md.slice(3))}`;
  return `${day(window.from)} – ${day(window.to)}`;
}
